import type { Cup } from "./types";
import type { World as SceneWorld } from "./scene";
import type { FocusHandle } from "./focus";
import type { InteractionHandles } from "./interaction";
import type { CatHandle } from "./cat";
import type { Hud } from "./hud";
import type * as THREE from "three";

// Single entry / exit path for a cup selection. Both pointer picking
// (interaction.onSelect) and search-result picks (hud.onSearchSelect)
// flow through the returned `selectCup` so focus mode, the info panel,
// and the cat's visibility stay in sync regardless of how the user got
// here. Pass `null` to exit focus mode.

export interface SelectionDeps {
  cupMap: Map<string, { group: THREE.Group; cup: Cup }>;
  hud: Hud;
  world: SceneWorld;
  focus: FocusHandle;
  interaction: InteractionHandles;
  /** Read live so a late-arriving cat (async GLB) gets its visibility
   * toggled too without main.ts re-wiring on cat load. */
  getCat: () => CatHandle | null;
  /** Scene-level container for the food + water bowls. Hidden along
   * with the cat and shelf in focus mode so only the cup and its
   * backdrop are visible. */
  poisGroup: THREE.Group;
}

export interface SelectionHandle {
  selectCup: (cup: Cup | null) => void;
}

export function attachSelection(deps: SelectionDeps): SelectionHandle {
  const { cupMap, hud, world, focus, interaction, getCat, poisGroup } = deps;

  function selectCup(cup: Cup | null): void {
    hud.showCup(cup);
    world.setBackground(cup?.backgroundUrl ?? null);
    if (cup) {
      const entry = cupMap.get(cup.id);
      if (entry) focus.enter(entry.group);
      hud.setBackVisible(true);
      hud.setResetVisible(false);
      // Lock cup picking + drag-to-throw while focused. The pointer
      // still drives OrbitControls so the user can rotate around the cup.
      interaction.setEnabled(false);
      getCat()?.setVisible(false);
      poisGroup.visible = false;
    } else {
      focus.exit();
      hud.setBackVisible(false);
      hud.setResetVisible(true);
      interaction.setEnabled(true);
      getCat()?.setVisible(true);
      poisGroup.visible = true;
    }
  }

  interaction.onSelect(selectCup);

  // Closing the info panel deselects the cup → clear the backdrop, drop
  // out of focus mode, and re-enable cup interaction.
  hud.onClose(() => {
    world.setBackground(null);
    focus.exit();
    hud.setBackVisible(false);
    hud.setResetVisible(true);
    interaction.setEnabled(true);
    getCat()?.setVisible(true);
    poisGroup.visible = true;
  });

  return { selectCup };
}
